import { environment } from 'environments';
import ReconnectingWebSocket from 'reconnecting-websocket';
import { setError } from 'state/Error';
import { ErrorType } from 'state/Error/types';
import { WidgetConfiguration } from 'state/Widget/types';
import { fetchCC } from 'utils/fetch';
import { dbGet, dbPut } from 'utils/storage';
import { ChannelDB, ConversationDB, ParticipantDB } from 'utils/storage/schemas';
import { v1 as uuidv1 } from 'uuid';

import {
  ChannelRequest,
  NotificationEventsType,
  NotificationSubscription,
  NotificationSubscriptionBody,
} from './types';

const PING_COMMAND = 'ping';
const PONG_COMMAND = 'pong';
const PING_INTERVAL_MS = 8000;
const PING_TIMEOUT_INTERVAL_MS = 6000;
const MAX_RETRY = 10;
const DEBUG = false;
const RETRY_BUMP = 800;

const pingRequests = new Map<number, ChannelRequest>();

let channelInformation: NotificationSubscription;
let participantKey: string;
let pingInterval;
let refreshInterval;
let correlationId = 0;
let retry = 0;
let registerRetry = 0;

const cleanup = () => {
  pingInterval && clearInterval(pingInterval);
  pingInterval = null;
  refreshInterval && clearInterval(refreshInterval);
  refreshInterval = null;
  pingRequests.forEach(request => {
    clearTimeout(request.timeout);
  });
  pingRequests.clear();
};

const register = async (
  configuration: WidgetConfiguration,
  information: NotificationSubscriptionBody
) => {
  try {
    const response = await fetchCC('participant-subscription', configuration, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(information),
    });
    return await response.json();
  } catch (e) {
    DEBUG && console.log('failed to fetch participant-subscription');
    return null;
  }
};

const reconnect = (reconnectingWebSocket: ReconnectingWebSocket, dispatch) => {
  DEBUG && console.log('reconnect');
  retry++;
  cleanup();
  if (retry > MAX_RETRY) {
    dispatch(
      setError({
        type: ErrorType.NOTIFICATION,
        message: 'Maximum number of retry reached establishing the web socket connection',
      })
    );
  } else {
    setTimeout(() => reconnectingWebSocket?.reconnect(), retry * RETRY_BUMP);
  }
};

const sendPingCommand = (reconnectingWebSocket: ReconnectingWebSocket, dispatch) => {
  correlationId++;

  const request = {
    correlationId: correlationId,
    command: PING_COMMAND,
  };

  DEBUG && console.log('sendPingCommand');

  return new Promise<unknown>((resolve, reject) => {
    const timeout = () => {
      DEBUG && console.log('ping timeout');
      pingRequests.delete(request.correlationId);
      dispatch(
        setError({
          type: ErrorType.NOTIFICATION,
          message: `Pong command not received after ${PING_TIMEOUT_INTERVAL_MS / 1000} seconds`,
        })
      );
      return reject();
    };

    const requestTimer = window.setTimeout(timeout, PING_TIMEOUT_INTERVAL_MS);

    const promise: ChannelRequest = {
      resolve,
      reject,
      command: PING_COMMAND,
      timeout: requestTimer,
    };

    pingRequests.set(correlationId, promise);
    DEBUG && console.log('send ping');
    reconnectingWebSocket?.send(JSON.stringify(request));
  });
};

const refreshSession = async () => {
  DEBUG && console.log('refreshSession');
  const options = {
    method: 'PUT',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };
  try {
    const response = await fetch(
      `${environment.api.notificationUrl}/${channelInformation.channelNickname}/${channelInformation.channelId}/channel-lifetime`,
      options
    );
    if (!response.ok) {
      throw new Error('could not refresh channel');
    }
    DEBUG && console.log('channel refreshed');
  } catch (e) {
    DEBUG && console.log('failed to refresh channel');
    DEBUG && console.log(e);
    throw e;
  }
};

export const deleteSession = async () => {
  participantKey = null;
  DEBUG && console.log('deleteSession');
  const options = {
    method: 'DELETE',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };
  try {
    await fetch(
      `${environment.api.notificationUrl}/${channelInformation.channelNickname}/${channelInformation.channelId}`,
      options
    );
    DEBUG && console.log('channel deleted');
  } catch (e) {
    DEBUG && console.log('failed to delete channel');
    DEBUG && console.log(e);
  }
};

const fetchSession = async () => {
  DEBUG && console.log('fetchSession');
  const tmpChannelInfo = await dbGet(ChannelDB);
  if (tmpChannelInfo) {
    channelInformation = tmpChannelInfo as NotificationSubscription;
    try {
      await refreshSession();
      return `${channelInformation.channelData.channelURL}`;
    } catch (e) {
      DEBUG && console.log('unable to recuperate channel');
      await deleteSession();
    }
  }

  const options = {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      channelLifetime: 3600,
    }),
  };

  cleanup();
  return fetch(`${environment.api.notificationUrl}/${uuidv1()}`, options)
    .then(response => {
      if (response.ok) {
        DEBUG && console.log('channel created');
        return response.json() as Promise<NotificationSubscription>;
      } else {
        DEBUG && console.log('channel creation failure');
        throw new Error(response.status.toString());
      }
    })
    .then(channel => {
      channelInformation = channel;
      dbPut(ChannelDB, { data: channel });
      return `${channel.channelData.channelURL}`;
    })
    .catch(err => {
      DEBUG && console.log(`channel creation error ${err.message}`);
      /* If we return undefined here the WebSocket won't try to reconnect since it
      throws an error in the constructor. We must return a valid websocket url */
      if (err === '403') return undefined;
      // Not authorized. Stop trying.
      else return 'wss://invalid-url-trying-to-reconnect';
    });
};

const handlePongCommand = (frame, dispatch) => {
  const promise = pingRequests.get(frame.correlationId);

  DEBUG && console.log('handlePongCommand');

  if (promise) {
    clearTimeout(promise.timeout);
    if (frame.code >= 200 && frame.code < 300) {
      DEBUG && console.log('ping successful');
      promise.resolve(frame.data);
    } else {
      DEBUG && console.log('ping failed');
      dispatch(
        setError({
          type: ErrorType.NOTIFICATION,
          message: 'Pong command received but with an error',
        })
      );
      promise.reject(frame.error);
    }
  }

  pingRequests.delete(frame.correlationId);
};

export const subscribe = (
  widgetConfiguration,
  participant,
  dispatch,
  subscriptionActive,
  onInitialFrame,
  onEventFrame
) => {
  const handleOpen = () => {
    DEBUG && console.log('handleOpen');
    if (registerRetry > MAX_RETRY) {
      dispatch(
        setError({
          type: ErrorType.NOTIFICATION,
          message: 'Maximum number of retry when subscribing the channel to the web chat service',
        })
      );
      registerRetry = 0;
      reconnect(reconnectingWebSocket, dispatch);
    } else {
      if (!subscriptionActive) return null;

      register(widgetConfiguration, {
        channelId: channelInformation.channelId,
        participant:
          {
            ...participant,
            ...(!participant?.participantExternalKey && { participantExternalKey: participantKey }),
          } ?? undefined,
      }).then(response => {
        if (response) {
          DEBUG && console.log('register successful');
          participantKey = response.participant?.participantExternalKey;
          dbPut(ParticipantDB, {
            data: {
              ...participant,
              ...(!participant?.participantExternalKey && {
                participantExternalKey: participantKey,
              }),
            },
            key: 'participantExternalKey',
            discriminator: widgetConfiguration.appKey,
          })
            .then(() =>
              dbPut(ConversationDB, {
                data: false,
                key: 'conversationTerminated',
              })
            )
            .catch((error: Error) =>
              dispatch(setError({ type: ErrorType.DATA, message: error.message }))
            );
          registerRetry = 0;
          cleanup();
          pingInterval = setInterval(async () => {
            try {
              await sendPingCommand(reconnectingWebSocket, dispatch);
              retry = 0;
            } catch (e) {
              DEBUG && console.log('ping failed with error');
              DEBUG && console.log(e);
              reconnect(reconnectingWebSocket, dispatch);
            }
          }, PING_INTERVAL_MS);
          refreshInterval = setInterval(async () => {
            await refreshSession();
          }, (channelInformation.channelLifetime - 60) * 1000);
        } else {
          DEBUG && console.log(`register failed, retrying in ${registerRetry * RETRY_BUMP}`);
          registerRetry++;
          setTimeout(() => {
            handleOpen();
          }, registerRetry * 500);
        }
      });
    }
  };

  const handleMessage = ({ data }) => {
    const frame = JSON.parse(data) as any;
    switch (frame.data.type) {
      case PONG_COMMAND:
        handlePongCommand(frame, dispatch);
        break;
      case NotificationEventsType.WEB_CHAT_INITIAL_STATE:
        onInitialFrame(frame.data);
        break;
      default:
        onEventFrame(frame.data.type, frame.data);
        break;
    }
  };

  const handleClose = event => {
    DEBUG && console.log(event);
  };

  const handleError = event => {
    DEBUG && console.log(event);
  };

  DEBUG && console.log('Creating web socket');
  const reconnectingWebSocket = new ReconnectingWebSocket(fetchSession, [], {
    maxRetries: 5,
    debug: DEBUG,
  });
  reconnectingWebSocket?.addEventListener('open', handleOpen);
  reconnectingWebSocket?.addEventListener('message', handleMessage);
  reconnectingWebSocket?.addEventListener('close', handleClose);
  reconnectingWebSocket?.addEventListener('error', handleError);

  return () => {
    DEBUG && console.log('Garbage cleaning');
    cleanup();
    reconnectingWebSocket?.removeEventListener('open', handleOpen);
    reconnectingWebSocket?.removeEventListener('message', handleMessage);
    reconnectingWebSocket?.removeEventListener('close', handleClose);
    reconnectingWebSocket?.removeEventListener('error', handleError);
    reconnectingWebSocket?.close();
  };
};
